import React from "react";
import { Alert, Button } from "@mui/material";
import { WorkflowFormSaveStatus } from "../../hooks/useWorkflowFormSave";

/** What the last Continue on a real form stage came back with, when it did not advance. */
export type WorkflowFormFeedback =
  | { kind: "invalid"; messages: string[] }
  | { kind: "forbidden" }
  | { kind: "submit-failed" }
  | { kind: "reload-failed" };

interface WorkflowRunFormBannerProps {
  saveStatus: WorkflowFormSaveStatus;
  feedback: WorkflowFormFeedback | null;
  onReload: () => void;
  onRetrySave: () => void;
  /** When given, a failed submit offers Retry (it resends the answers held for it) instead of
   *  asking for Continue again — for screens like pif-1 that can't keep their typed values. */
  onRetrySubmit?: () => void;
}

/**
 * The one banner above a real form stage's actions. A conflict outranks everything else: until
 * the case is reloaded, any further save or submit would 409 again.
 */
const WorkflowRunFormBanner: React.FC<WorkflowRunFormBannerProps> = ({
  saveStatus,
  feedback,
  onReload,
  onRetrySave,
  onRetrySubmit,
}) => {
  const reloadAction = (
    <Button color="inherit" size="small" onClick={onReload}>
      Reload
    </Button>
  );

  if (saveStatus === "conflict") {
    return (
      <Alert severity="warning" className="workflow-run__alert" action={reloadAction}>
        This case was updated by someone else. Reload to see the latest.
      </Alert>
    );
  }
  if (feedback?.kind === "reload-failed") {
    return (
      <Alert severity="warning" className="workflow-run__alert" action={reloadAction}>
        Your answers were submitted, but the next step couldn&apos;t be loaded.
      </Alert>
    );
  }
  if (saveStatus === "failed") {
    return (
      <Alert
        severity="error"
        className="workflow-run__alert"
        action={
          <Button color="inherit" size="small" onClick={onRetrySave}>
            Retry
          </Button>
        }
      >
        Your changes couldn&apos;t be saved. They&apos;re still here — try again.
      </Alert>
    );
  }
  if (feedback?.kind === "submit-failed" && onRetrySubmit) {
    return (
      <Alert
        severity="error"
        className="workflow-run__alert"
        action={
          <Button color="inherit" size="small" onClick={onRetrySubmit}>
            Retry
          </Button>
        }
      >
        Your answers couldn&apos;t be saved to this case. They&apos;re kept — try again.
      </Alert>
    );
  }
  if (feedback?.kind === "submit-failed") {
    return (
      <Alert severity="error" className="workflow-run__alert">
        This step couldn&apos;t be submitted. Your answers are still here — try Continue again.
      </Alert>
    );
  }
  if (feedback?.kind === "forbidden") {
    return (
      <Alert severity="error" className="workflow-run__alert">
        This step is assigned to someone else, so you can&apos;t submit it.
      </Alert>
    );
  }
  if (feedback?.kind === "invalid") {
    return (
      <Alert severity="error" className="workflow-run__alert">
        Fix the highlighted fields, then continue.
        {feedback.messages.map((message) => (
          <div key={message}>{message}</div>
        ))}
      </Alert>
    );
  }
  return null;
};

export default WorkflowRunFormBanner;
